import React, { useCallback, useEffect, useState } from 'react'
import Grid from '@mui/material/Grid'
import { Box, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import ArticleIcon from '@mui/icons-material/Article'
import CommentIcon from '@mui/icons-material/Comment'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { toast } from 'react-toastify'
import DashboardServices from 'services/DashboardServices'

const USER_ID_FILTER_ROLES = [
    'Subcommittee Secretary',
    'Subcommittee Member',
    'Committee Secretary',
    'Committee Member',
    'Subcommittee Chair'
]

const normalizeRole = (role = '') => String(role).trim().replace(/\s+/g, ' ').toLowerCase()

const KPI_CARDS_CONFIG = [
    {
        key: 'total_documents_assigned',
        label: 'Assigned Documents',
        subtitle: 'this month',
        icon: <ArticleIcon fontSize="medium" />,
        iconBg: '#1565C0',
    },
    {
        key: 'active_users',
        label: 'Active Users',
        subtitle: 'Registered',
        icon: <CommentIcon fontSize="medium" />,
        iconBg: '#7B1FA2',
    },
    {
        key: 'pending_deadlines_uploaded',
        label: 'Pending Deadlines',
        subtitle: 'requires attention',
        icon: <AccessTimeIcon fontSize="medium" />,
        iconBg: '#E65100',
    },
    {
        key: 'subcommittees_count',
        label: 'Subcommittees',
        subtitle: 'this cycle',
        icon: <CheckCircleIcon fontSize="medium" />,
        iconBg: '#2E7D32',
    },
]

const ROLE_BASED_OPEN_COMMENTS_CARD = {
    key: 'open_comments',
    label: 'Open Comments',
    subtitle: 'requires attention',
    icon: <CommentIcon fontSize="medium" />,
    iconBg: '#7B1FA2',
}

const KPICard = ({ label, subtitle, icon, iconBg, value, loading }) => {
    const theme = useTheme()

    return (
        <Box
            sx={{
                backgroundColor: theme.palette.background.paper,
                borderRadius: 3,
                boxShadow: '0 1px 6px rgba(0, 0, 0, 0.2)',
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                height: '100%',
            }}
        >
            <Box
                sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '10px',
                    backgroundColor: iconBg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    mb: 1,
                }}
            >
                {icon}
            </Box>

            <Typography
                variant="h3"
                fontWeight={700}
                sx={{ lineHeight: 1, color: theme.palette.text.primary,fontSize: '1.875rem' }}
            >
                {loading ? '—' : (value ?? 0)}
            </Typography>

            <Typography
                variant="body1"
                fontWeight={600}
                sx={{ color: theme.palette.text.primary,fontSize: '0.875rem' }}
            >
                {label}
            </Typography>

            <Typography variant="caption" sx={{ color: theme.palette.text.secondary,fontSize: '0.75rem' }}>
                {loading ? '...' : subtitle}
            </Typography>
        </Box>
    )
}

const KPICards = ({ filters = {}, userRoles = [] }) => {
    const [kpis, setKpis] = useState(null)
    const [loading, setLoading] = useState(false)

    const canViewOpenComments = userRoles
        .map((role) => normalizeRole(role?.name || role))
        .some((role) => USER_ID_FILTER_ROLES.map(normalizeRole).includes(role))

    const cardsToRender = canViewOpenComments
        ? KPI_CARDS_CONFIG.map((card) => (card.key === 'active_users' ? ROLE_BASED_OPEN_COMMENTS_CARD : card))
        : KPI_CARDS_CONFIG

    const fetchSummary = useCallback(async () => {
        setLoading(true)
        try {
            const response = await DashboardServices.getSummary(filters)
            if (response.success) {
                setKpis(response?.data?.data?.kpis || null)
            } else {
                toast.error(response.message || 'Failed to load dashboard summary.')
            }
        } finally {
            setLoading(false)
        }
    }, [filters])

    useEffect(() => {
        fetchSummary()
    }, [fetchSummary])

    return (
        <Grid container spacing={2}>
            {cardsToRender.map((card) => (
                <Grid key={card.key} size={{ lg: 3, md: 6, sm: 12, xs: 12 }}>
                    <KPICard
                        label={card.label}
                        subtitle={card.subtitle}
                        icon={card.icon}
                        iconBg={card.iconBg}
                        value={kpis?.[card.key]}
                        loading={loading}
                    />
                </Grid>
            ))}
        </Grid>
    )
}

export default KPICards
